import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Briefcase, ShieldCheck, Sparkles, Megaphone, FlaskConical,
  Handshake, ArrowRight, Check, AlertCircle, ChevronDown,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const FALLBACK = {
  eyebrow: "PARTNER WITH 2nd OPINION",
  title: "Build the future of health with India's trusted second-opinion network.",
  subtitle: "From pharmaceutical OEMs to nutraceutical innovators — list your verified products beside our specialists and reach patients who already trust our brand.",
  cta: "Talk to our partnerships team",
  value_props: [],
  how_it_works: [],
  faq: [],
};

const ICONS = [ShieldCheck, Sparkles, Megaphone, Handshake];

export default function B2B() {
  const [content, setContent] = useState(FALLBACK);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    website: "", interest: "general", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [faqOpen, setFaqOpen] = useState(null);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/storefront/content", { params: { kind: "b2b" } })
      .then((r) => setContent({ ...FALLBACK, ...(r.data?.data || {}) }))
      .catch(() => {});
  }, []);

  // deep-link: /b2b#contact scrolls down
  useEffect(() => {
    if (loc.hash === "#contact") {
      const el = document.getElementById("b2b-contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loc.hash, content]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/b2b/inquiries", form);
      setDone(true);
      setForm({ name: "", company: "", email: "", phone: "", website: "", interest: "general", message: "" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="b2b-wrap" data-testid="b2b-page">
      {/* HERO */}
      <section className="b2b-hero">
        <div className="b2b-hero-in">
          <span className="b2b-eyebrow">{content.eyebrow}</span>
          <h1 className="b2b-h1">{content.title}</h1>
          <p className="b2b-sub">{content.subtitle}</p>
          <div className="b2b-cta-row">
            <a href="#b2b-contact" className="pg-btn pg-btn-ink" data-testid="b2b-cta-contact">
              <Handshake size={16} /> {content.cta}
            </a>
            <button className="pg-btn pg-btn-ghost" onClick={() => nav("/pharmaceuticals")} data-testid="b2b-cta-pharma">
              See pharmacy storefront <ArrowRight size={15} />
            </button>
          </div>
          <div className="b2b-trust-row">
            <span><ShieldCheck size={14} /> NMC-verified specialists</span>
            <span><Check size={14} /> DPDP Act 2023 aligned</span>
            <span><Check size={14} /> Pan-India distribution</span>
          </div>
        </div>
        <div className="b2b-hero-ornament" aria-hidden>
          <div className="b2b-blob b1" />
          <div className="b2b-blob b2" />
          <div className="b2b-blob b3" />
        </div>
      </section>

      {/* VALUE PROPS */}
      {content.value_props?.length > 0 && (
        <section className="b2b-section">
          <div className="b2b-section-in">
            <h2 className="b2b-h2">Why brands collaborate with us</h2>
            <div className="b2b-grid">
              {content.value_props.map((v, i) => {
                const Icon = ICONS[i % ICONS.length];
                return (
                  <div className="b2b-card" key={i} data-testid={`b2b-value-${i}`}>
                    <span className="b2b-card-ic"><Icon size={18} /></span>
                    <h3>{v.title}</h3>
                    <p>{v.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      {content.how_it_works?.length > 0 && (
        <section className="b2b-section alt">
          <div className="b2b-section-in">
            <h2 className="b2b-h2">From hello to live in days, not months</h2>
            <div className="b2b-steps">
              {content.how_it_works.map((s, i) => (
                <div className="b2b-step" key={i} data-testid={`b2b-step-${i}`}>
                  <div className="b2b-step-n">{String(i + 1).padStart(2, "0")}</div>
                  <h3>{s.title.replace(/^\d+\.\s*/, "")}</h3>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT FORM */}
      <section className="b2b-section" id="b2b-contact">
        <div className="b2b-contact-wrap">
          <div className="b2b-contact-copy">
            <span className="b2b-eyebrow">PARTNERSHIPS</span>
            <h2 className="b2b-h2">Tell us about your brand.</h2>
            <p className="b2b-sub">
              Drop your details — our partnerships lead will reply within two working days
              with a curated next-step plan, NDA template and onboarding checklist.
            </p>
            <ul className="b2b-bullets">
              <li><FlaskConical size={14} /> Pharmaceutical & medical-device brands</li>
              <li><Sparkles size={14} /> Nutraceuticals & functional-food innovators</li>
              <li><Briefcase size={14} /> Distributors, CROs and clinical labs</li>
            </ul>
          </div>
          <form className="b2b-form" onSubmit={submit} data-testid="b2b-form">
            {done ? (
              <div className="b2b-success" data-testid="b2b-form-success">
                <Check size={20} />
                <div>
                  <b>Thanks — we've got it.</b>
                  <p>Our partnerships team will reach out within 2 working days.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="b2b-row2">
                  <label>Your name
                    <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="b2b-input-name" placeholder="Ravi Kumar" />
                  </label>
                  <label>Company
                    <input required value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="b2b-input-company" placeholder="NovaPharma Pvt Ltd" />
                  </label>
                </div>
                <div className="b2b-row2">
                  <label>Work email
                    <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="b2b-input-email" placeholder="ravi@nova.com" />
                  </label>
                  <label>Phone
                    <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="b2b-input-phone" placeholder="+91 98000 00000" />
                  </label>
                </div>
                <div className="b2b-row2">
                  <label>Website (optional)
                    <input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="b2b-input-website" placeholder="https://nova.com" />
                  </label>
                  <label>Interest
                    <select value={form.interest} onChange={(e) => set("interest", e.target.value)} data-testid="b2b-input-interest">
                      <option value="general">General partnership</option>
                      <option value="pharmaceutical">Pharmaceuticals & Pharmacy</option>
                      <option value="nutraceutical">Nutraceuticals</option>
                      <option value="device">Medical devices</option>
                      <option value="diagnostics">Diagnostics / labs</option>
                    </select>
                  </label>
                </div>
                <label>Tell us about your products & goals
                  <textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="b2b-input-message" placeholder="What you sell, where you're licensed, what success looks like for us together…" />
                </label>
                {err && <p className="b2b-err" data-testid="b2b-form-error"><AlertCircle size={14} /> {err}</p>}
                <button className="pg-btn pg-btn-ink" type="submit" disabled={busy} data-testid="b2b-form-submit">
                  {busy ? "Sending…" : "Send inquiry"} <ArrowRight size={16} />
                </button>
              </>
            )}
          </form>
        </div>
      </section>

      {/* FAQ */}
      {content.faq?.length > 0 && (
        <section className="b2b-section alt">
          <div className="b2b-section-in narrow">
            <h2 className="b2b-h2">Frequently asked</h2>
            <div className="b2b-faq">
              {content.faq.map((f, i) => (
                <div className={`b2b-faq-item ${faqOpen === i ? "open" : ""}`} key={i}>
                  <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} data-testid={`b2b-faq-${i}`}>
                    <span>{f.q}</span>
                    <ChevronDown size={16} />
                  </button>
                  {faqOpen === i && <p>{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
